import React, { useEffect, useCallback, useState } from "react";
import { FieldArrayRenderProps } from "react-final-form-arrays";
import {
  Record,
  Datagrid,
  Pagination,
  ResourceContextProvider,
  Identifier,
  useListContext,
} from "react-admin";
import { Card } from "@material-ui/core";
import { difference, isEmpty } from "lodash";
import { CompactListBase, Sort } from "../../form/CompactListBase";
import { SelectAllHandler } from "./SelectAllHandler";

interface Props extends Partial<FieldArrayRenderProps<any, HTMLElement>> {
  reference: string;
  children: React.ReactNode;
  record?: Record;
  source?: string;
  sort?: Sort;
  filter?: any;
}

interface SelectedItemsHandlerProps extends Partial<Props> {
  handleSelectedItems: (ids: Array<Identifier>) => void;
}

const SelectedItemsHandler = ({
  handleSelectedItems,
  record,
  source,
}: SelectedItemsHandlerProps) => {
  const [initialized, setInitialized] = useState<boolean>(false);
  const { selectedIds, onSelect } = useListContext();
  useEffect(() => {
    if (!initialized) {
      return;
    }

    handleSelectedItems(selectedIds);
  }, [selectedIds, handleSelectedItems, initialized]);

  useEffect(() => {
    if (initialized) {
      return;
    }

    // Effect to set the initial selected items based on the editing record
    if (!record) {
      return;
    }

    if (!source) {
      return;
    }

    if (isEmpty(record)) {
      onSelect([]);
      setInitialized(true);
      return;
    }

    if (!record[source]) {
      return;
    }

    onSelect(record[source] ?? []);
    setInitialized(true);
  }, [record, source, onSelect, initialized]);

  return null;
};

export const ListIterator = ({
  fields,
  reference,
  record,
  source,
  children,
  sort = { field: "id", order: "asc" },
  filter,
}: Props) => {
  const handleSelectedItems = useCallback(
    (ids: Array<Identifier>) => {
      if (!fields) {
        console.warn("fields was undefined in ListInput.handleSelectedItems");
        return;
      }

      const toAdd = difference<Identifier>(ids, fields.value);
      const toRemove = difference<Identifier>(fields.value, ids);

      const indicesToRemove = [];
      for (const item of toRemove) {
        const index = fields.value.indexOf(item);
        if (index === -1) {
          console.warn(
            `Tried to remove the item '${item}', but it was not found in fields.value`,
            { currentValues: fields.value }
          );
          continue;
        }

        indicesToRemove.push(index);
      }

      // Sort in descending order to remove items in one go and avoid multiple effects
      indicesToRemove.sort((a, b) => b - a);
      for (const index of indicesToRemove) {
        fields.remove(index);
      }

      for (const item of toAdd) {
        fields.push(item);
      }
    },
    [fields]
  );

  return (
    <ResourceContextProvider value={reference}>
      <CompactListBase
        resource={reference}
        basePath={`/${reference}`}
        perPage={25}
        sort={sort}
        filter={filter}
      >
        <SelectedItemsHandler
          handleSelectedItems={handleSelectedItems}
          fields={fields}
          record={record}
          source={source}
        />
        <Card>
          <SelectAllHandler resource={reference} filter={filter} />
          <Datagrid hasBulkActions>{children}</Datagrid>
          <Pagination rowsPerPageOptions={[25, 50, 100]} />
        </Card>
      </CompactListBase>
    </ResourceContextProvider>
  );
};
